import {
  UPLOAD_LEADER_CHANNEL_PREFIX,
  UPLOAD_LEADER_ELECTION_DELAY_MS,
  UPLOAD_LEADER_HEARTBEAT_MS,
  UPLOAD_LEADER_LOCK_PREFIX,
  UPLOAD_TAB_INACTIVE_STALE_MS,
  UPLOAD_TAB_PRESENCE_PREFIX,
  UPLOAD_WEB_LOCK_RETRY_MS,
} from '@/features/uploads/config/uploadCoordination';
import { setUploadLeadership } from '../stores/uploadLeadershipStore';

interface TabPresence {
  uid: string;
  tabId: string;
  openedAt: number;
  lastSeenAt: number;
  hasActiveUploads: boolean;
}

interface LeaderMessage {
  version: 1;
  uid: string;
  sourceTabId: string;
  type: 'leader-active' | 'leader-released';
}

type CoordinationMode = 'none' | 'web-locks' | 'presence';

const tabId = (() => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  globalThis.crypto?.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
})();

let generation = 0;
let activeUid: string | null = null;
let mode: CoordinationMode = 'none';
let openedAt = 0;
let hasActiveUploads = false;
let hasLeadership = false;
let releaseWebLock: (() => void) | null = null;
let webLockAttemptInFlight = false;
let webLockRetryTimer: ReturnType<typeof setInterval> | null = null;
let presenceHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
let channel: BroadcastChannel | null = null;

function lockName(uid: string): string {
  return `${UPLOAD_LEADER_LOCK_PREFIX}:${uid}`;
}

function presenceKey(uid: string, id = tabId): string {
  return `${UPLOAD_TAB_PRESENCE_PREFIX}:${uid}:${id}`;
}

function postLeaderMessage(type: LeaderMessage['type']): void {
  if (!activeUid) return;
  channel?.postMessage({
    version: 1,
    uid: activeUid,
    sourceTabId: tabId,
    type,
  } satisfies LeaderMessage);
}

function setLeader(uid: string): void {
  const shouldAnnounce = !hasLeadership;
  hasLeadership = true;
  setUploadLeadership(uid, 'leader');
  if (shouldAnnounce) postLeaderMessage('leader-active');
}

function setFollower(uid: string): void {
  if (hasLeadership) postLeaderMessage('leader-released');
  hasLeadership = false;
  setUploadLeadership(uid, 'follower');
}

function closeChannel(): void {
  channel?.close();
  channel = null;
}

function tryAcquireWebLock(uid: string, currentGeneration: number): Promise<void> {
  const lockManager = globalThis.navigator?.locks;
  if (!lockManager || hasLeadership || webLockAttemptInFlight || generation !== currentGeneration) {
    return Promise.resolve();
  }
  webLockAttemptInFlight = true;
  return new Promise((initialAttemptFinished) => {
    const fallbackToPresence = () => {
      webLockAttemptInFlight = false;
      initialAttemptFinished();
      if (generation === currentGeneration && activeUid === uid) {
        void startPresenceCoordination(uid);
      }
    };
    try {
      void lockManager
        .request(lockName(uid), { mode: 'exclusive', ifAvailable: true }, async (lock) => {
          webLockAttemptInFlight = false;
          initialAttemptFinished();
          if (generation !== currentGeneration || activeUid !== uid) return;
          if (!lock) {
            setFollower(uid);
            return;
          }

          setLeader(uid);
          await new Promise<void>((resolve) => {
            releaseWebLock = resolve;
          });
          releaseWebLock = null;
        })
        .catch(fallbackToPresence);
    } catch {
      fallbackToPresence();
    }
  });
}

function openChannel(uid: string, currentGeneration: number): void {
  closeChannel();
  if (!('BroadcastChannel' in globalThis)) return;
  try {
    channel = new BroadcastChannel(`${UPLOAD_LEADER_CHANNEL_PREFIX}:${uid}`);
  } catch {
    channel = null;
    return;
  }
  channel.addEventListener('message', (event: MessageEvent<LeaderMessage>) => {
    const message = event.data;
    if (
      generation !== currentGeneration ||
      activeUid !== uid ||
      message?.version !== 1 ||
      message.uid !== uid ||
      message.sourceTabId === tabId
    ) {
      return;
    }
    if (message.type === 'leader-released' && mode === 'web-locks' && !hasLeadership) {
      void tryAcquireWebLock(uid, currentGeneration);
    }
  });
}

function readPresence(uid: string): TabPresence[] {
  const now = Date.now();
  const records: TabPresence[] = [];
  for (let index = localStorage.length - 1; index >= 0; index -= 1) {
    const key = localStorage.key(index);
    if (!key?.startsWith(`${UPLOAD_TAB_PRESENCE_PREFIX}:${uid}:`)) continue;
    try {
      const record = JSON.parse(localStorage.getItem(key) ?? '') as Partial<TabPresence>;
      if (
        record.uid !== uid ||
        typeof record.tabId !== 'string' ||
        typeof record.openedAt !== 'number' ||
        typeof record.lastSeenAt !== 'number' ||
        typeof record.hasActiveUploads !== 'boolean'
      ) {
        localStorage.removeItem(key);
        continue;
      }
      if (now - record.lastSeenAt > UPLOAD_TAB_INACTIVE_STALE_MS) {
        localStorage.removeItem(key);
        continue;
      }
      records.push(record as TabPresence);
    } catch {
      localStorage.removeItem(key);
    }
  }
  return records;
}

function writePresence(uid: string): void {
  const record: TabPresence = {
    uid,
    tabId,
    openedAt,
    lastSeenAt: Date.now(),
    hasActiveUploads,
  };
  localStorage.setItem(presenceKey(uid), JSON.stringify(record));
}

function electPresenceLeader(uid: string): void {
  if (activeUid !== uid || mode !== 'presence') return;
  const records = readPresence(uid).sort(
    (left, right) => left.openedAt - right.openedAt || left.tabId.localeCompare(right.tabId),
  );
  if (records[0]?.tabId === tabId) setLeader(uid);
  else setFollower(uid);
}

function onStorage(event: StorageEvent): void {
  if (!activeUid || mode !== 'presence') return;
  if (event.key?.startsWith(`${UPLOAD_TAB_PRESENCE_PREFIX}:${activeUid}:`)) {
    electPresenceLeader(activeUid);
  }
}

async function startPresenceCoordination(uid: string): Promise<void> {
  if (activeUid !== uid) return;
  if (mode === 'presence' && presenceHeartbeatTimer) return;
  mode = 'presence';
  if (webLockRetryTimer) clearInterval(webLockRetryTimer);
  webLockRetryTimer = null;
  try {
    writePresence(uid);
    globalThis.addEventListener?.('storage', onStorage);
    presenceHeartbeatTimer = setInterval(() => {
      if (activeUid !== uid || mode !== 'presence') return;
      try {
        writePresence(uid);
        electPresenceLeader(uid);
      } catch {
        setUploadLeadership(uid, 'unsupported');
      }
    }, UPLOAD_LEADER_HEARTBEAT_MS);
    await new Promise((resolve) => setTimeout(resolve, UPLOAD_LEADER_ELECTION_DELAY_MS));
    if (activeUid === uid && mode === 'presence') electPresenceLeader(uid);
  } catch {
    setUploadLeadership(uid, 'unsupported');
  }
}

function onPageHide(event: PageTransitionEvent): void {
  const uid = activeUid;
  stopUploadTabCoordination();
  if (event.persisted && uid) {
    globalThis.addEventListener?.(
      'pageshow',
      () => {
        void startUploadTabCoordination(uid);
      },
      { once: true },
    );
  }
}

/**
 * Elect exactly one mutable upload tab for a Firebase user on this origin. Modern browsers use one
 * page-lifetime Web Lock; older browsers use deterministic localStorage presence records. Stale
 * fallback records expire even if the departed tab last reported active work, so another tab can
 * recover the queue instead of leaving the user permanently read-only.
 */
export async function startUploadTabCoordination(uid: string): Promise<void> {
  if (activeUid === uid && mode !== 'none') return;
  stopUploadTabCoordination();
  generation += 1;
  const currentGeneration = generation;
  activeUid = uid;
  openedAt = Date.now();
  hasActiveUploads = false;
  hasLeadership = false;
  setUploadLeadership(uid, 'initializing');
  openChannel(uid, currentGeneration);
  globalThis.addEventListener?.('pagehide', onPageHide, { once: true });

  if (globalThis.navigator?.locks) {
    mode = 'web-locks';
    await tryAcquireWebLock(uid, currentGeneration);
    if (mode === 'web-locks') {
      webLockRetryTimer = setInterval(
        () => void tryAcquireWebLock(uid, currentGeneration),
        UPLOAD_WEB_LOCK_RETRY_MS,
      );
    }
    return;
  }

  await startPresenceCoordination(uid);
}

export function setUploadTabHasActiveUploads(active: boolean): void {
  if (hasActiveUploads === active) return;
  hasActiveUploads = active;
  if (activeUid && mode === 'presence') {
    try {
      writePresence(activeUid);
    } catch {
      setUploadLeadership(activeUid, 'unsupported');
    }
  }
}

export function stopUploadTabCoordination(): void {
  generation += 1;
  if (activeUid && hasLeadership) postLeaderMessage('leader-released');
  releaseWebLock?.();
  releaseWebLock = null;
  if (webLockRetryTimer) clearInterval(webLockRetryTimer);
  if (presenceHeartbeatTimer) clearInterval(presenceHeartbeatTimer);
  webLockRetryTimer = null;
  presenceHeartbeatTimer = null;
  globalThis.removeEventListener?.('storage', onStorage);
  globalThis.removeEventListener?.('pagehide', onPageHide);
  if (activeUid && mode === 'presence') {
    try {
      localStorage.removeItem(presenceKey(activeUid));
    } catch {
      // Storage may be disabled while the page is closing; the inactive record is cleaned later.
    }
  }
  closeChannel();
  activeUid = null;
  mode = 'none';
  hasActiveUploads = false;
  hasLeadership = false;
  webLockAttemptInFlight = false;
  setUploadLeadership(null, 'inactive');
}
